import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { PaypalService } from '../../../services/paypal.service';

interface Payment {
  payerName?: string;
  amount?: number;
  frequency?: string;
  currency?: string;
  paypalId?: string;
  remark?: string;
  planId: string;
  planStatus?: string;
  isoDate?: string;
}

@Component({
    selector: 'app-pyapal-plan-list',
    imports: [CommonModule, RouterModule, ConfirmDialogComponent],
    templateUrl: './plan-list.component.html',
    styleUrls: ['./plan-list.component.css']
})
export class PaypalPlanListComponent implements OnInit {

  payments: Payment[] = [];
  filteredPayments: Payment[] = [];

  loading = false;
  error: string | null = null;

  pageSize = 8;
  currentPage = 1;

  deletingMap: { [key: string]: boolean | undefined } = {};

  showDeleteModal = false;
  modalPlanId: string | null = null;
  modalPlanLabel = '';
  modalError: string | null = null;
  isDeleting = false;

  constructor(private paypalService: PaypalService) { }

  async ngOnInit() {
    await this.paypalService.getAccessToken();
    await this.loadPaymentsFromPaypal();
  }

  get pagedPayments(): Payment[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredPayments.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredPayments.length / this.pageSize));
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  goToPage(p: number) { if (p >= 1 && p <= this.totalPages) this.currentPage = p; }
  nextPage() { this.goToPage(this.currentPage + 1); }
  prevPage() { this.goToPage(this.currentPage - 1); }

  async loadPaymentsFromPaypal(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      //Fetch all products
      const products = await this.paypalService.getAllProducts();
      if (!products?.length) {
        this.payments = [];
        this.filteredPayments = [];
        return;
      }

      //Fetch all plans for products
      const allPlans = (
        await Promise.all(
          products.map((p: any) => this.paypalService.getPlansByProduct(p.id))
        )
      ).flat().filter(Boolean);

      if (!allPlans.length) {
        this.payments = [];
        this.filteredPayments = [];
        return;
      }

      //Unique plan IDs
      const uniquePlanIds = Array.from(new Set(allPlans.map((p: any) => p.id)));

      //Fetch plan details
      const planDetails = await Promise.all(
        uniquePlanIds.map(id =>
          this.paypalService.getPlanDetails(id)
            .then(detail => ({ id, detail }))
            .catch(() => ({ id, detail: null }))
        )
      );

      const detailMap: Record<string, any> = {};
      planDetails.forEach(p => {
        if (p.detail) detailMap[p.id] = p.detail;
      });

      this.payments = uniquePlanIds
        .map(planId => {
          const pd = detailMap[planId];
          if (!pd) return null;

          const productInfo =
            products.find((x: any) => x.id === pd.product_id) || {};

          const billing = pd.billing_cycles?.[0];
          const price = billing?.pricing_scheme?.fixed_price || {};

          const amount = Number(price?.value || 0);
          const currency =
            price?.currency_code ||
            price?.currency ||
            price?.currencyCode ||
            '';

          const interval = billing?.frequency?.interval_unit || '';
          const count = billing?.frequency?.interval_count || '';
          const frequency = count && interval ? `${count} ${interval}` : 'N/A';

          return {
            payerName: productInfo.name,
            amount,
            currency,
            frequency,
            remark: productInfo.description,
            paypalId: pd.product_id,
            isoDate: productInfo.create_time,
            planId,
            planStatus: pd.status
          } as Payment;
        })
        .filter((x): x is Payment => x !== null);

      // 6️⃣ Only ACTIVE plans
      this.payments = this.payments.filter(x => x.planStatus === 'ACTIVE');

      // 7️⃣ Sort newest first
      this.payments.sort((a, b) =>
        new Date(b.isoDate || '').getTime() -
        new Date(a.isoDate || '').getTime()
      );

      this.filteredPayments = [...this.payments];

    } catch (err) {
      console.error('loadPaymentsFromPaypal error:', err);
      this.error = 'Products/Plans fetch error';
    } finally {
      this.loading = false;
    }
  }

  openDeleteModal(planId?: string, planLabel?: string) {
    if (!planId) {
      this.modalError = 'This product has no plan to delete.';
      this.showDeleteModal = true;
      return;
    }
    this.modalPlanId = planId;
    this.modalPlanLabel = planLabel || planId;
    this.modalError = null;
    this.showDeleteModal = true;
  }

  onDeletePlanClick(planId?: string) {
    this.openDeleteModal(planId);
  }

  closeDeleteModal(force = false) {
    if (this.isDeleting && !force) return;

    this.showDeleteModal = false;
    this.modalPlanId = null;
    this.modalPlanLabel = '';
    this.modalError = null;
  }

  async confirmDelete() {
    if (!this.modalPlanId) return;

    this.isDeleting = true;
    this.modalError = null;

    const planId = this.modalPlanId;
    this.deletingMap[planId] = true;

    try {
      const planDetail = await this.paypalService.getPlanDetails(planId);
      const currentStatus = planDetail?.status || '';

      if (currentStatus !== 'ACTIVE') {
        this.updateLocalPlanStatus(planId, currentStatus);
        throw new Error(`Cannot deactivate plan: status is "${currentStatus}".`);
      }

      await this.paypalService.deactivatePlan(planId);

      this.closeDeleteModal(true);
      await this.loadPaymentsFromPaypal();

    } catch (err: any) {
      console.error('confirmDelete error:', err);
      this.modalError = err?.message || 'Delete failed. See console.';
      this.showDeleteModal = true;
    } finally {
      this.isDeleting = false;
      this.deletingMap[planId] = false;
    }
  }

  private updateLocalPlanStatus(planId: string | null, newStatus: string) {
    if (!planId) return;

    this.payments = this.payments.map(p =>
      p.planId === planId ? { ...p, planStatus: newStatus } : p
    );

    this.filteredPayments = this.filteredPayments.map(p =>
      p.planId === planId ? { ...p, planStatus: newStatus } : p
    );
  }
}
